"use client";

import { useState, useCallback, useRef } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Info,
  AlertTriangle,
  TrendingUp,
  Building2,
  Calculator,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import {
  type MesaiTipi,
  type FazlaMesaiSonuc,
  BRUT_ASGARI_UCRET,
  GUNLUK_CALISMA_SURESI,
  YILLIK_FAZLA_MESAI_SINIRI,
  MESAI_TIPI_SECENEKLERI,
  hesaplaFazlaMesai,
  formatTL,
  SAATLIK_BOLEN,
  YILI,
} from "./fazla-mesai-engine";

// ─── HowTo Schema ─────────────────────────────────────────────
const howToSchema = generateHowToSchema({
  title: "Fazla Mesai Ücreti Nasıl Hesaplanır?",
  description:
    "Brüt maaş ve fazla mesai saatinizi girerek 2026 İş Kanunu'na uygun fazla mesai ücretinizi hesaplayın.",
  totalTime: "PT2M",
  steps: [
    {
      name: "Mesai Tipini Seçin",
      text: "Fazla çalışma, hafta tatili, resmi tatil veya gece mesaisi gibi çalışma tipini seçin.",
    },
    {
      name: "Brüt Maaşınızı Girin",
      text: "Aylık brüt maaşınızı TL olarak girin. Asgari ücret butonu ile 2026 asgari ücreti otomatik doldurabilirsiniz.",
    },
    {
      name: "Fazla Mesai Saatini Girin",
      text: "Aylık toplam fazla mesai saatinizi girin.",
    },
    {
      name: "Sonuçları İnceleyin",
      text: "Brüt ve net fazla mesai ücretiniz, SGK kesintileri ve işveren maliyeti detaylı olarak gösterilir.",
    },
  ],
});

// ─── FAQ Bileşeni ─────────────────────────────────────────────
interface SSSSoruProps {
  soru: string;
  cevap: string;
}

function SSSSoru({ soru, cevap }: SSSSoruProps) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

// ─── Sonuç Satırı Bileşeni ───────────────────────────────────
interface SonucSatirProps {
  label: string;
  value: string;
  variant?: "normal" | "kesinti" | "eklenti" | "toplam";
  bold?: boolean;
}

function SonucSatir({
  label,
  value,
  variant = "normal",
  bold = false,
}: SonucSatirProps) {
  const renkler = {
    normal: "text-gray-900",
    kesinti: "text-red-600",
    eklenti: "text-blue-600",
    toplam: "text-emerald-600",
  };
  return (
    <div
      className={`flex justify-between items-center py-1.5 ${bold ? "pt-3 border-t border-gray-200" : ""}`}
    >
      <span
        className={`text-sm ${bold ? "font-bold text-gray-900" : "text-gray-600"}`}
      >
        {label}
      </span>
      <span
        className={`text-sm font-medium tabular-nums ${bold ? "text-base font-bold" : ""} ${renkler[variant]}`}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Ana Bileşen ──────────────────────────────────────────────
export default function FazlaMesaiHesaplayiciClient() {
  // State
  const [mesaiTipi, setMesaiTipi] = useState<MesaiTipi>("fazla_calisma");
  const [brutMaas, setBrutMaas] = useState("");
  const [mesaiSaati, setMesaiSaati] = useState("");
  const [sonuc, setSonuc] = useState<FazlaMesaiSonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [hesaplandi, setHesaplandi] = useState(false);

  // Refs - Focus loss prevention
  const maasInputRef = useRef<HTMLInputElement>(null);
  const saatInputRef = useRef<HTMLInputElement>(null);

  // Hesaplama
  const hesapla = useCallback(() => {
    setHata(null);

    const maas = parseFloat(brutMaas);
    const saat = parseFloat(mesaiSaati);

    if (!brutMaas || isNaN(maas) || maas <= 0) {
      setHata("Lütfen geçerli bir brüt maaş giriniz.");
      setSonuc(null);
      setHesaplandi(false);
      return;
    }

    if (!mesaiSaati || isNaN(saat) || saat <= 0) {
      setHata("Lütfen geçerli bir fazla mesai saati giriniz.");
      setSonuc(null);
      setHesaplandi(false);
      return;
    }

    if (saat > 744) {
      setHata(
        "Bir ayda en fazla 744 saat olabilir. Lütfen değeri kontrol ediniz."
      );
      setSonuc(null);
      setHesaplandi(false);
      return;
    }

    const hesapSonuc = hesaplaFazlaMesai({
      brutMaas: maas,
      mesaiSaati: saat,
      mesaiTipi,
      haftalikSure: "45_saat",
    });

    setSonuc(hesapSonuc);
    setHesaplandi(true);
    setHata(null);
  }, [brutMaas, mesaiSaati, mesaiTipi]);

  // Asgari ücret otomatik doldurma
  const asgariUcretDoldur = useCallback(() => {
    setBrutMaas(BRUT_ASGARI_UCRET.toString());
  }, []);

  // Sıfırla
  const sifirla = useCallback(() => {
    setBrutMaas("");
    setMesaiSaati("");
    setSonuc(null);
    setHata(null);
    setHesaplandi(false);
  }, []);

  // Enter tuşu ile hesaplama
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        hesapla();
      }
    },
    [hesapla]
  );

  const asgariSaatlikUcret = BRUT_ASGARI_UCRET / SAATLIK_BOLEN;
  const resmiTatilSaatlikUcret = asgariSaatlikUcret * 2;
  const gunlukResmiTatilUcreti = resmiTatilSaatlikUcret * GUNLUK_CALISMA_SURESI;

  return (
    <>
      <StructuredData data={[howToSchema]} />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* ─── Hero ─────────────────────────────────────────── */}
        <section className="bg-gradient-to-br from-orange-500 to-amber-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "Fazla Mesai Hesaplayıcı",
                  href: "/araclar/fazla-mesai-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />

            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <Clock className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  Fazla Mesai Ücreti Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  {YILI} Güncel
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              İş Kanunu Madde 41&apos;e göre fazla mesai ücretinizi hesaplayın.
              Haftalık 45 saat üzeri, hafta tatili, resmi tatil ve gece mesaisi
              dahil.
            </p>
          </div>
        </section>

        {/* ─── Hesaplama Formu ──────────────────────────────── */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* Mesai Tipi Seçimi */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Mesai Tipini Seçin
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {MESAI_TIPI_SECENEKLERI.map((secenek) => (
                    <button
                      key={secenek.value}
                      type="button"
                      onClick={() => {
                        setMesaiTipi(secenek.value);
                        if (hesaplandi) hesapla();
                      }}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                        mesaiTipi === secenek.value
                          ? "border-orange-500 bg-orange-50 ring-1 ring-orange-500"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      {mesaiTipi === secenek.value && (
                        <CheckCircle2 className="absolute top-3 right-3 w-5 h-5 text-orange-500" />
                      )}
                      <span className="block text-sm font-semibold text-gray-900 mb-1 pr-6">
                        {secenek.label}
                      </span>
                      <span className="block text-xs text-gray-500 mb-2">
                        {secenek.aciklama}
                      </span>
                      <span className="inline-block px-2 py-0.5 bg-gray-100 rounded text-xs font-medium text-gray-700">
                        {secenek.katsayiLabel}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Brüt Maaş ve Mesai Saati */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {/* Brüt Maaş */}
                <div>
                  <label
                    htmlFor="brutMaas"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Aylık Brüt Maaş
                  </label>
                  <div className="relative">
                    <input
                      ref={maasInputRef}
                      id="brutMaas"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="100"
                      value={brutMaas}
                      onChange={(e) => setBrutMaas(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="0,00"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900 placeholder-gray-400 transition-shadow"
                      aria-label="Aylık brüt maaş"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                      ₺
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={asgariUcretDoldur}
                    className="mt-2 text-xs text-orange-600 hover:text-orange-700 font-medium transition-colors"
                  >
                    {YILI} Asgari Ücret Uygula ({formatTL(BRUT_ASGARI_UCRET)})
                  </button>
                </div>

                {/* Fazla Mesai Saati */}
                <div>
                  <label
                    htmlFor="mesaiSaati"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Fazla Mesai Saati (Aylık)
                  </label>
                  <div className="relative">
                    <input
                      ref={saatInputRef}
                      id="mesaiSaati"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      max="744"
                      step="0.5"
                      value={mesaiSaati}
                      onChange={(e) => setMesaiSaati(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="0"
                      className="w-full px-4 py-3 pr-16 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900 placeholder-gray-400 transition-shadow"
                      aria-label="Fazla mesai saati"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                      Saat
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Yıllık sınır: {YILLIK_FAZLA_MESAI_SINIRI} saat (İş Kanunu
                    Md. 41)
                  </p>
                </div>
              </div>

              {/* Hata Mesajı */}
              {hata && (
                <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{hata}</p>
                </div>
              )}

              {/* Butonlar */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={hesapla}
                  className="flex-1 bg-orange-500 text-white py-4 rounded-xl font-semibold hover:bg-orange-600 active:bg-orange-700 transition-colors shadow-sm"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Hesapla
                  </span>
                </button>
                {hesaplandi && (
                  <button
                    type="button"
                    onClick={sifirla}
                    className="px-6 py-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Sıfırla
                  </button>
                )}
              </div>
            </div>

            {/* ─── Sonuç Alanı ───────────────────────────────── */}
            {sonuc && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                {/* Yıllık sınır uyarısı */}
                {sonuc.yillikSinirUyarisi && (
                  <div className="mb-6 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">
                        Yıllık Fazla Mesai Sınırı Aşıldı
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        İş Kanunu Md. 41&apos;e göre yıllık fazla mesai{" "}
                        {YILLIK_FAZLA_MESAI_SINIRI} saati geçemez. Girdiğiniz
                        değer bu sınırın üzerindedir.
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {/* Net Mesai Ücreti Özeti */}
                  <div className="bg-emerald-600 text-white rounded-xl p-5 flex flex-col justify-between">
                    <span className="text-sm text-emerald-100 font-medium">
                      Net Fazla Mesai Ücreti
                    </span>
                    <span className="text-2xl sm:text-3xl font-bold mt-2 tabular-nums">
                      {formatTL(sonuc.netMesaiUcreti)}
                    </span>
                  </div>
                  {/* Brüt Mesai Ücreti */}
                  <div className="bg-white rounded-xl p-5 border border-gray-200 flex flex-col justify-between">
                    <span className="text-sm text-gray-500 font-medium">
                      Brüt Fazla Mesai Ücreti
                    </span>
                    <span className="text-2xl font-bold text-gray-900 mt-2 tabular-nums">
                      {formatTL(sonuc.toplamBrutMesaiUcreti)}
                    </span>
                  </div>
                  {/* İşveren Maliyeti */}
                  <div className="bg-white rounded-xl p-5 border border-gray-200 flex flex-col justify-between">
                    <span className="text-sm text-gray-500 font-medium">
                      Toplam İşveren Maliyeti
                    </span>
                    <span className="text-2xl font-bold text-blue-600 mt-2 tabular-nums">
                      {formatTL(sonuc.toplamIsverenMaliyet)}
                    </span>
                  </div>
                </div>

                {/* Detaylı Dökümler */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* İşçi Hesabı */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      <h3 className="font-bold text-gray-900">İşçi Hesabı</h3>
                    </div>
                    <div className="space-y-0.5">
                      <SonucSatir
                        label={`Saatlik brüt ücret (÷225)`}
                        value={formatTL(sonuc.saatlikBrutUcret)}
                      />
                      <SonucSatir
                        label={`Mesai saatlik ücreti (×${sonuc.katsayi})`}
                        value={formatTL(sonuc.mesaiSaatlikBrutUcret)}
                      />
                      <SonucSatir
                        label={`Brüt mesai (${sonuc.mesaiSaati} saat)`}
                        value={formatTL(sonuc.toplamBrutMesaiUcreti)}
                        bold
                      />
                      <SonucSatir
                        label="SGK işçi payı (%14)"
                        value={`-${formatTL(sonuc.sgkIsciKesinti)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="İşsizlik sigortası (%1)"
                        value={`-${formatTL(sonuc.issizlikIsciKesinti)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="Gelir vergisi (%15)"
                        value={`-${formatTL(sonuc.gelirVergisi)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="Damga vergisi (%0,759)"
                        value={`-${formatTL(sonuc.damgaVergisi)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="Net Fazla Mesai Ücreti"
                        value={formatTL(sonuc.netMesaiUcreti)}
                        variant="toplam"
                        bold
                      />
                    </div>
                  </div>

                  {/* İşveren Maliyeti */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Building2 className="w-5 h-5 text-blue-600" />
                      <h3 className="font-bold text-gray-900">
                        İşveren Maliyeti
                      </h3>
                    </div>
                    <div className="space-y-0.5">
                      <SonucSatir
                        label="Brüt fazla mesai ücreti"
                        value={formatTL(sonuc.toplamBrutMesaiUcreti)}
                      />
                      <SonucSatir
                        label="SGK işveren payı (%21,75)"
                        value={`+${formatTL(sonuc.sgkIsverenMaliyet)}`}
                        variant="eklenti"
                      />
                      <SonucSatir
                        label="İşsizlik işveren payı (%2)"
                        value={`+${formatTL(sonuc.issizlikIsverenMaliyet)}`}
                        variant="eklenti"
                      />
                      <SonucSatir
                        label="Toplam İşveren Maliyeti"
                        value={formatTL(sonuc.toplamIsverenMaliyet)}
                        variant="eklenti"
                        bold
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ─── Resmi Tatil Özel Blokları ───────────────────── */}
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-orange-100 bg-orange-50 p-5">
              <div className="mb-3 inline-flex rounded-full bg-white px-3 py-1 text-sm font-semibold text-orange-700">
                23 Nisan 2026
              </div>
              <h2 className="mb-3 text-xl font-bold text-gray-900">
                23 Nisan mesai ücreti nasıl hesaplanır?
              </h2>
              <p className="mb-4 text-sm leading-relaxed text-gray-700">
                23 Nisan resmi tatildir. Çalışan o gün çalışırsa normal günlük
                ücreti korunur; ayrıca çalıştığı saat için saatlik brüt ücretin
                2 katı ek mesai ücreti hesaplanır.
              </p>
              <div className="space-y-2 rounded-xl bg-white p-4 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">
                    2026 asgari saatlik brüt
                  </span>
                  <strong className="text-gray-900">
                    {formatTL(asgariSaatlikUcret)}
                  </strong>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Resmi tatil katsayısı</span>
                  <strong className="text-gray-900">×2</strong>
                </div>
                <div className="flex justify-between gap-4 border-t border-gray-100 pt-2">
                  <span className="text-gray-900">Saatlik ek brüt ücret</span>
                  <strong className="text-orange-700">
                    {formatTL(resmiTatilSaatlikUcret)}
                  </strong>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-5">
              <div className="mb-3 inline-flex rounded-full bg-white px-3 py-1 text-sm font-semibold text-amber-700">
                1 Mayıs 2026
              </div>
              <h2 className="mb-3 text-xl font-bold text-gray-900">
                1 Mayıs mesai ücreti nasıl hesaplanır?
              </h2>
              <p className="mb-4 text-sm leading-relaxed text-gray-700">
                1 Mayıs Emek ve Dayanışma Günü de resmi tatildir. Mesai
                hesabında aynı ×2 resmi tatil katsayısı kullanılır; net sonuç
                çalışanın vergi matrahına göre değişebilir.
              </p>
              <div className="space-y-2 rounded-xl bg-white p-4 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Günlük örnek süre</span>
                  <strong className="text-gray-900">
                    {GUNLUK_CALISMA_SURESI} saat
                  </strong>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Saatlik ek brüt ücret</span>
                  <strong className="text-gray-900">
                    {formatTL(resmiTatilSaatlikUcret)}
                  </strong>
                </div>
                <div className="flex justify-between gap-4 border-t border-gray-100 pt-2">
                  <span className="text-gray-900">Günlük ek brüt örnek</span>
                  <strong className="text-amber-700">
                    {formatTL(gunlukResmiTatilUcreti)}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Bilgilendirme Notu ─────────────────────────── */}
          <div className="mt-8 p-5 bg-amber-50 rounded-xl border border-amber-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Önemli Bilgiler
                </h3>
                <ul className="text-sm text-gray-600 space-y-1.5">
                  <li>
                    • Bu hesaplama {YILI} yılı güncel oranlarına göre
                    yapılmaktadır.
                  </li>
                  <li>
                    • Gelir vergisi, ilk dilim (%15) üzerinden hesaplanmıştır.
                    Kümülatif matrah durumuna göre farklılık gösterebilir.
                  </li>
                  <li>
                    • Fazla mesai için işçinin yazılı onayı gereklidir (İş
                    Kanunu Md. 41).
                  </li>
                  <li>
                    • Yıllık toplam fazla mesai süresi{" "}
                    {YILLIK_FAZLA_MESAI_SINIRI} saati aşamaz.
                  </li>
                  <li>
                    • Kesin hesaplama için muhasebe veya İK departmanınıza
                    danışınız.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ─── İçerik / Rehber ──────────────────────────────── */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <div className="prose prose-gray max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Fazla Mesai Ücreti Hesaplama Rehberi ({YILI})
            </h2>

            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm space-y-8">
              {/* Yasal Dayanak */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Yasal Dayanak
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  4857 sayılı İş Kanunu&apos;nun 41. maddesine göre, haftalık 45
                  saati aşan çalışmalar &quot;fazla çalışma&quot; olarak kabul
                  edilir. Fazla çalışma yapan işçiye, her fazla çalışma saati
                  için normal saat ücretinin yüzde elli (%50) zamlı ücreti
                  ödenir.
                </p>
              </div>

              {/* Hesaplama Formülü */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Hesaplama Formülü
                </h3>
                <div className="bg-orange-50 rounded-xl p-5 space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-orange-700 whitespace-nowrap">
                      Saatlik Ücret:
                    </span>
                    <span className="text-gray-700">Aylık Brüt Maaş ÷ 225</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-orange-700 whitespace-nowrap">
                      Fazla Mesai:
                    </span>
                    <span className="text-gray-700">
                      Saatlik Ücret × 1,5 × Mesai Saati
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-orange-700 whitespace-nowrap">
                      Hafta Tatili:
                    </span>
                    <span className="text-gray-700">
                      Saatlik Ücret × 2 × Mesai Saati
                    </span>
                  </div>
                </div>
              </div>

              {/* Mesai Tipleri Tablosu */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Mesai Tipleri ve Katsayılar
                </h3>
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-3 font-semibold text-gray-900">
                          Mesai Tipi
                        </th>
                        <th className="text-left py-3 px-3 font-semibold text-gray-900">
                          Katsayı
                        </th>
                        <th className="text-left py-3 px-3 font-semibold text-gray-900">
                          Yasal Dayanak
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr>
                        <td className="py-3 px-3 text-gray-700">
                          Fazla Çalışma
                        </td>
                        <td className="py-3 px-3 font-medium text-orange-600">
                          ×1,5
                        </td>
                        <td className="py-3 px-3 text-gray-500">
                          İş Kanunu Md. 41
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-3 text-gray-700">
                          Fazla Sürelerle Çalışma
                        </td>
                        <td className="py-3 px-3 font-medium text-orange-600">
                          ×1,25
                        </td>
                        <td className="py-3 px-3 text-gray-500">
                          İş Kanunu Md. 41/3
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-3 text-gray-700">
                          Hafta Tatili
                        </td>
                        <td className="py-3 px-3 font-medium text-orange-600">
                          ×2
                        </td>
                        <td className="py-3 px-3 text-gray-500">
                          İş Kanunu Md. 46
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-3 text-gray-700">
                          Resmi Tatil / Bayram
                        </td>
                        <td className="py-3 px-3 font-medium text-orange-600">
                          ×2
                        </td>
                        <td className="py-3 px-3 text-gray-500">
                          İş Kanunu Md. 47
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 px-3 text-gray-700">
                          Gece Mesaisi
                        </td>
                        <td className="py-3 px-3 font-medium text-orange-600">
                          ×1,5
                        </td>
                        <td className="py-3 px-3 text-gray-500">
                          İş Kanunu Md. 69
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* SSS */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Sık Sorulan Sorular
                </h3>
                <div className="space-y-3">
                  <SSSSoru
                    soru="Fazla mesai ücreti nasıl hesaplanır?"
                    cevap="Saatlik brüt ücret = Aylık brüt maaş ÷ 225. Fazla mesai saatlik ücreti = Saatlik brüt ücret × 1,5. Toplam brüt fazla mesai ücreti = Mesai saatlik ücreti × toplam saat. Net ücret için SGK (%14+%1), gelir vergisi ve damga vergisi düşülür."
                  />
                  <SSSSoru
                    soru="Fazla sürelerle çalışma nedir?"
                    cevap="İş sözleşmesiyle haftalık çalışma süresi 45 saatin altında belirlenmişse, bu süreyi aşan ancak 45 saati geçmeyen çalışmalar 'fazla sürelerle çalışma' sayılır. Ücreti, normal saat ücretinin %25 fazlasıdır (×1,25)."
                  />
                  <SSSSoru
                    soru="Yıllık fazla mesai sınırı kaç saattir?"
                    cevap={`4857 sayılı İş Kanunu'nun 41. maddesine göre, yıllık toplam fazla mesai süresi ${YILLIK_FAZLA_MESAI_SINIRI} saati aşamaz. Bu sınırı aşan işveren idari para cezasıyla karşılaşabilir.`}
                  />
                  <SSSSoru
                    soru="Hafta tatilinde çalışma ücreti nasıl hesaplanır?"
                    cevap="Hafta tatilinde çalışan işçiye, normal günlük ücretine ek olarak çalıştığı her saat için normal saat ücretinin 2 katı ödenir. Yani toplam olarak 3 günlük ücret hak eder (1 tatil ücreti + 2 kat çalışma ücreti)."
                  />
                  <SSSSoru
                    soru={`${YILI} asgari ücret üzerinden fazla mesai ne kadardır?`}
                    cevap={`${YILI} brüt asgari ücret ${formatTL(BRUT_ASGARI_UCRET)}'dir. Saatlik brüt ücret: ${formatTL(BRUT_ASGARI_UCRET / 225)}. Normal fazla mesai (×1,5): ${formatTL((BRUT_ASGARI_UCRET / 225) * 1.5)} / saat. Hafta tatili (×2): ${formatTL((BRUT_ASGARI_UCRET / 225) * 2)} / saat.`}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Related Links ───────────────────────────────── */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="fazla-mesai-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        {/* ─── CTA ─────────────────────────────────────────── */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-indigo-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              Mesai Takibini Otomatikleştirin
            </h2>
            <p className="text-indigo-100 mb-6 max-w-xl mx-auto">
              IKAI ile fazla mesai takibi, otomatik ücret hesaplama ve yasal
              uyum kontrolü tek platformda.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-indigo-700 px-6 py-3 rounded-xl font-semibold hover:bg-indigo-50 transition-colors"
            >
              Ücretsiz Deneyin
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
